import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { RbacFormField } from '../../../../shared/component/rbac-form/rbac-form-field.model';
import { RbacFormComponent } from '../../../../shared/component/rbac-form/rbac-form.component';
import { RbacModalComponent } from '../../../../shared/component/rbac-modal/rbac-modal.component';
import { RbacTableColumn } from '../../../../shared/component/rbac-table/rbac-table-column.model';
import { RbacTableComponent } from '../../../../shared/component/rbac-table/rbac-table.component';
import { RoleService } from '../../../../shared/services/rbackservices/role.service';
import { Role, HIERARCHICAL_ROUTES, flattenRoutes } from '../rbac.models';
import { ApiCallsService } from '../../../../shared/services/api-calls.service';
import { ToastService } from '../../../../shared/services/toast.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-role-management',
  standalone: true,
  imports: [CommonModule, RbacTableComponent, RbacFormComponent, RbacModalComponent],
  templateUrl: './role-management.component.html',
  styleUrls: ['./role-management.component.scss']
})
export class RoleManagementComponent implements OnInit {
  roles: Role[] = [];
  loading = false;
  saving = false;
  modalOpen = false;
  editingRole: Role | null = null;

  columns: RbacTableColumn[] = [
    { key: 'name', label: 'Role Name' },
    { key: 'type', label: 'Login Type' },
    { key: 'mode', label: 'Mode' },
    { key: 'login_mode', label: 'Login Mode' },
    { key: 'category', label: 'Category' },
    { key: 'status', label: 'Status', type: 'status' }
  ];

  fields: RbacFormField[] = [
    { key: 'name', label: 'Role Name', type: 'text', required: true, placeholder: 'e.g. Super Admin' },
    { key: 'type', disabledOnEdit: true, label: 'Type', type: 'text', required: true, placeholder: 'e.g. AA' },
    { key: 'landing_page', label: 'Landing Page', type: 'select', placeholder: 'Select landing page...', options: flattenRoutes(HIERARCHICAL_ROUTES) },
    { key: 'mode', label: 'Mode', type: 'text', placeholder: 'e.g. WEB' },
    {
      key: 'login_mode', disabledOnEdit: true, NoPayloadEdit: false, label: 'Login Mode', type: 'select', placeholder: 'GRID/DG', options: [
        { label: 'Normal', value: '' },
        { label: 'GRID', value: 'GRID' },
        { label: 'DG', value: 'DG' }
      ]
    },
    { key: 'category', label: 'Category', type: 'select', placeholder: 'Select category types...', options: [] }
  ];

  // --- Role Category management ---
  categories: any[] = [];
  categoryLoading = false;
  categorySaving = false;
  categoryModalOpen = false;
  editingCategory: any = null;

  categoryColumns: RbacTableColumn[] = [
    { key: 'name', label: 'Category Name' },
    { key: 'category_type', label: 'Category Type' }
  ];

  categoryFields: RbacFormField[] = [
    { key: 'name', label: 'Category Name', type: 'text', required: true, placeholder: 'e.g. All Site' },
    { key: 'category_type', label: 'Category Type', type: 'text', required: true, placeholder: 'e.g. 1' }
  ];

  constructor(private roleService: RoleService, private router: Router, private apiservice: ApiCallsService, private toast: ToastService) { }

  ngOnInit(): void {
    this.loadRoles();
    this.loadCategories();
  }

  loadRoles(): void {
    this.loading = true;
    this.roleService
      .get()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => (this.roles = res?.Data ?? res ?? []),
        error: () => (this.roles = [])
      });
  }

  private loadCategories(): void {
    this.categoryLoading = true;
    this.apiservice.getCategories()
      .pipe(finalize(() => (this.categoryLoading = false)))
      .subscribe({
        next: (res) => {
          const categories = res?.Data ?? res ?? [];
          this.categories = categories;

          // Role form's Category dropdown: show name, submit category_type
          const categoryField = this.fields.find(f => f.key === 'category');
          if (categoryField) {
            categoryField.options = categories.map((c: any) => ({
              label: c.name,
              value: c.category_type
            }));
            this.fields = [...this.fields]; // new array reference so the form re-renders if it's already open
          }
        },
        error: () => (this.categories = [])
      });
  }

  openAdd(): void {
    this.editingRole = null;
    this.modalOpen = true;
  }

  openEdit(role: Role): void {
    this.editingRole = role;
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
    this.editingRole = null;
  }

  onSave(value: Record<string, any>): void {
    this.saving = true;
    const { _id, type, ...rest } = value;
    const isUpdate = !!value['_id'];

    const payload: any = isUpdate
      ? { ...rest, application: 'AMR' }           // update: no type
      : { ...rest, type, application: 'AMR' };     // add: keep type

    const action = isUpdate ? 'Updated' : 'Added';

    const request$ = isUpdate
      ? this.roleService.update({ id: _id, ...payload })
      : this.roleService.add(payload as Role);

    request$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: (res: any) => {
        if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
          const errorMsg = res?.Message || res?.message || `Failed to ${action.toLowerCase()} Role`;
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
          return;
        }
        this.toast.showToast(`Role ${action} Successfully`, 'colored', 5000, 'success');
        this.closeModal();
        this.loadRoles();
      },
      error: err => {
        const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || `Failed to ${action.toLowerCase()} Role`;
        this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
      }
    });
  }
  onToggleStatus(role: Role): void {
    if (!role.id) { return; }
    const nextStatus = role.status === 'N' ? 'Y' : 'N';
    this.roleService.update({ id: role.id, status: nextStatus }).subscribe({
      next: (res: any) => {
        if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
          const errorMsg = res?.Message || res?.message || 'Failed to update status';
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
          return;
        }
        this.loadRoles();
      },
      error: err => {
        const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || 'Failed to update status';
        this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
      }
    });
  }

  /** Soft delete: sets status to 'N' via PUT — no DELETE endpoint called. */
  onDelete(role: Role): void {
    const id = role._id || role.id;
    if (!id) { return; }
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you really want to delete role "${role.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'No, cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6'
    }).then((result) => {
      if (!result.isConfirmed) {
        this.toast.showToast('Action cancelled', 'colored', 3000, 'info');
        return;
      }
      this.roleService.update({ id, status: 'N' }).subscribe({
        next: (res: any) => {
          if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
            const errorMsg = res?.Message || res?.message || 'Failed to deactivate role';
            this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
            return;
          }
          this.toast.showToast('Role deleted successfully', 'colored', 5000, 'success');
          this.loadRoles();
        },
        error: err => {
          const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || 'Failed to deactivate role';
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
        }
      });
    });
  }

  /** Navigate to the Permission page for this role's login type. */
  private normalizeRoleName(name: string): string {
    const trimmed = (name || '').trim();
    if (trimmed.toLowerCase() === 'super admin') return 'super_admin';
    return trimmed.toLowerCase().replace(/\s+/g, '_');
  }

  navigateToPermissions(role: Role): void {
    const roleKey = this.normalizeRoleName(role.name);
    const encodedId = btoa(role._id || '');
    localStorage.setItem(roleKey, encodedId);
    this.router.navigate(['permission', roleKey], { queryParamsHandling: 'preserve' });
  }
  // --- Role Category CRUD ---

  openAddCategory(): void {
    this.editingCategory = null;
    this.categoryModalOpen = true;
  }

  openEditCategory(category: any): void {
    this.editingCategory = category;
    this.categoryModalOpen = true;
  }

  closeCategoryModal(): void {
    this.categoryModalOpen = false;
    this.editingCategory = null;
  }

  onSaveCategory(value: Record<string, any>): void {
    this.categorySaving = true;
    const payload = {
      name: value['name'],
      category_type: Number(value['category_type'])
    };

    // id mirrors name, per API convention
    const request$ = this.editingCategory
      ? this.apiservice.updateCategory({ id: this.editingCategory.name, ...payload })
      : this.apiservice.addCategory(payload);

    request$.pipe(finalize(() => (this.categorySaving = false))).subscribe({
      next: (res: any) => {
        if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
          const errorMsg = res?.Message || res?.message || 'Failed to save category';
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
          return;
        }
        this.closeCategoryModal();
        this.loadCategories();
      },
      error: err => {
        const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || 'Failed to save category';
        this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
      }
    });
  }

  onDeleteCategory(category: any): void {
    if (!category) { return; }
    const id = category._id || category.id;
    if (!id) { return; }
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you really want to delete role category "${category.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'No, cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6'
    }).then((result) => {
      if (!result.isConfirmed) {
        this.toast.showToast('Action cancelled', 'colored', 3000, 'info');
        return;
      }

      const payload: any = {
        id: id,
        name: category.name,
        category_type: Number(category.category_type),
        status: 'N'
      };

      this.apiservice.updateCategory(payload).subscribe({
        next: (res: any) => {
          if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
            const errorMsg = res?.Message || res?.message || 'Failed to deactivate category';
            this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
            return;
          }
          this.toast.showToast('Category Deactivated Successfully', 'colored', 5000, 'success');
          this.loadCategories();
        },
        error: err => {
          const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || 'Failed to deactivate category';
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
        }
      });
    });
  }
}